import React from 'react'
import { Calendar, Euro, Radio } from 'lucide-react'

const featureBoxes = [
  {
    icon: Calendar,
    title: 'Manage Dates',
    description: 'Set rolling enrollment or fixed start dates for your cohorts.',
  },
  {
    icon: Euro,
    title: 'Flexible Pricing',
    description: 'Offer early-bird discounts and payment plans to students.',
  },
  {
    icon: Radio,
    title: 'SEO Visibility',
    description: 'Your listings are automatically optimized for search engines.',
  },
]

const ListingSchool: React.FC = () => (
  <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10">
    <nav className="mb-6">
      <ol className="flex flex-wrap items-center gap-2 text-sm text-gray-500">
        <li>Docs</li>
        <li>/</li>
        <li className="text-gray-900 font-medium">Listing your school</li>
      </ol>
    </nav>

    <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
      Listing Your School & <span className="text-primary-500">Certifications</span>
    </h1>
    <p className="text-gray-600 mb-8 sm:mb-12 leading-relaxed text-sm sm:text-base">
      Categorize and market your teacher training programs effectively. Our platform makes it easy to showcase your offerings with flexible options for dates, pricing, and visibility.
    </p>

    <div className="flex flex-col sm:flex-row items-start gap-4 mb-8">
      <span className="flex-shrink-0 w-10 h-10 bg-primary-500 text-white font-bold text-sm flex items-center justify-center rounded">
        01
      </span>
      <div className="flex-1 min-w-0 w-full">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Key Features</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {featureBoxes.map((box) => {
            const Icon = box.icon
            return (
              <div
                key={box.title}
                className="bg-white border border-gray-200 rounded-xl p-4 sm:p-6 hover:border-emerald-200 hover:shadow-md transition-all min-w-0 overflow-hidden"
              >
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg bg-emerald-50 flex items-center justify-center mb-3 sm:mb-4 shrink-0">
                  <Icon className="h-5 w-5 sm:h-6 sm:w-6 text-emerald-600" />
                </div>
                <h3 className="font-bold text-gray-900 mb-2 text-sm sm:text-base break-words">{box.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed break-words">{box.description}</p>
              </div>
            )
          })}
        </div>
      </div>
    </div>

    <div className="flex flex-col sm:flex-row items-start gap-4">
      <span className="flex-shrink-0 w-10 h-10 bg-primary-500 text-white font-bold text-sm flex items-center justify-center rounded">
        02
      </span>
      <div className="flex-1 min-w-0 w-full">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Steps to list your school</h2>
        <div className="bg-gray-50 rounded-xl p-4 sm:p-6 border border-gray-100">
          <ol className="space-y-3">
            <li className="flex gap-3 min-w-0">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary-100 text-primary-600 font-semibold text-xs flex items-center justify-center">1</span>
              <span className="text-sm text-gray-700 break-words min-w-0">Complete your school profile in the Profile section with address, contact details, and studio gallery.</span>
            </li>
            <li className="flex gap-3 min-w-0">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary-100 text-primary-600 font-semibold text-xs flex items-center justify-center">2</span>
              <span className="text-sm text-gray-700 break-words min-w-0">Add your courses under Courses → Create Course. Include program type, duration, curriculum, and accommodation options.</span>
            </li>
            <li className="flex gap-3 min-w-0">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary-100 text-primary-600 font-semibold text-xs flex items-center justify-center">3</span>
              <span className="text-sm text-gray-700 break-words min-w-0">Set pricing for each room type (private, shared, etc.) and optional meal plans.</span>
            </li>
            <li className="flex gap-3 min-w-0">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary-100 text-primary-600 font-semibold text-xs flex items-center justify-center">4</span>
              <span className="text-sm text-gray-700 break-words min-w-0">Submit for verification. Once approved, your listings go live and appear in search results.</span>
            </li>
          </ol>
        </div>
      </div>
    </div>
  </div>
)

export default ListingSchool
